import { ABTestAssignment, ABTestAssignments } from '../api/ReplicantAPI';
import { type ABTestsDynamicConfig, isPlaceboTestId, isTestRolledOutToUser } from '../common/ReplicantABTests';
import DB, { ABTestUserListConfig } from '../db/DB';
import { ReplicantConfig } from '../ReplicantConfig';
import { pickABTestBucket, Ruleset } from '../ReplicantRuleset';

export default class ServerReplicantABTests {
    private cache:
        | { timestamp: number; promise: Promise<{ abTests: ABTestsDynamicConfig; userLists: ABTestUserListConfig[] }> }
        | undefined;

    constructor(
        private opts: {
            config: ReplicantConfig<any>;
            db: DB<any>;
            devOpts: { abTestsCacheExpiresInMs: number; dateNow: () => number };
        },
    ) {}

    async getAssignments(opts: {
        isNewUser: boolean;
        userId: string;
        currentAssignments: Record<string, ABTestAssignment | undefined>;
        userState: object | undefined;
    }): Promise<ABTestAssignments | undefined> {
        const { isNewUser, userId, currentAssignments, userState } = opts;

        const ruleset: Ruleset['abTests'] = this.opts.config.ruleset?.abTests;
        if (!ruleset) {
            return undefined;
        }

        const result: ABTestAssignments = {};

        const dynamicConfig = await this.getDynamicConfig();

        for (const testId in ruleset) {
            const assignment = this.getAssignment({
                dynamicConfig,
                isNewUser,
                userId,
                testId,
                ruleset: ruleset[testId]!,
                currentAssignments,
                userState,
            });

            if (assignment !== undefined) {
                result[testId] = assignment;
            }
        }

        return result;
    }

    async getDynamicConfig(): Promise<{ abTests: ABTestsDynamicConfig; userLists: ABTestUserListConfig[] }> {
        const now = this.opts.devOpts.dateNow();

        if (!this.cache || now - this.cache.timestamp > this.opts.devOpts.abTestsCacheExpiresInMs) {
            const cachePromise = Promise.all([this.opts.db.abTests.get(), this.opts.db.abTests.userLists.get()])
                .then(([abTests, userLists]) => ({ abTests, userLists }))
                .catch((error) => {
                    delete this.cache; // Clear cache if DB read fails
                    throw error;
                });

            this.cache = {
                promise: cachePromise,
                timestamp: now,
            };
        }

        return this.cache.promise;
    }

    private getAssignment(opts: {
        isNewUser: boolean;
        userId: string;
        testId: string;
        dynamicConfig: { abTests: ABTestsDynamicConfig; userLists: ABTestUserListConfig[] };
        ruleset: NonNullable<Ruleset['abTests']>[string];
        currentAssignments: Record<string, ABTestAssignment | undefined>;
        userState: object | undefined;
    }): ABTestAssignment | undefined {
        const { testId, userId } = opts;
        const isDynamicallyConfigurableTest = !isPlaceboTestId(testId); // Activation, roll-out or user lists do not apply to placebo tests
        const testConfig = opts.dynamicConfig.abTests[testId];

        if (isDynamicallyConfigurableTest) {
            if (!testConfig?.active) {
                return undefined; // Test inactive. Do not assign to anybody.
            }

            if (testConfig.stopAssignment) {
                return { type: 'stopped', bucketId: '' };
            }

            // User lists may be set up to force a specific bucket.
            for (const userList of opts.dynamicConfig.userLists) {
                if (!userList.userIds.includes(userId)) {
                    continue;
                }

                const userListAppliesToTest = userList.testIds === undefined || userList.testIds.includes(testId);
                if (!userListAppliesToTest) {
                    continue;
                }

                for (const bucketId of userList.bucketIds) {
                    if (opts.ruleset.buckets.find((bucket) => bucket.id === bucketId)) {
                        return { type: 'automatic', bucketId, isByUserList: true, newUsersOnly: false };
                    }
                }
            }

            // Each user has a min roll-out value necessary to participate in a given test.
            // As the configured roll-out increases, more users will participate.
            const rollOut =
                opts.isNewUser && testConfig.newUsersRollOut !== undefined
                    ? testConfig.newUsersRollOut
                    : testConfig.rollOut;

            if (!isTestRolledOutToUser({ rollOut, testId, userId })) {
                const isRolledOutToNewUser =
                    testConfig.newUsersRollOut !== undefined &&
                    isTestRolledOutToUser({ rollOut: testConfig.newUsersRollOut, testId, userId });

                if (!isRolledOutToNewUser) {
                    return undefined;
                }
            }

            // If we were already manually assigned, use that assignment
            const currentAssignment = opts.currentAssignments[testId];
            if (currentAssignment?.type === 'manual-active' && !!currentAssignment?.newUsersOnly === opts.isNewUser) {
                return currentAssignment;
            }

            if (opts.ruleset.dependsOn) {
                const allDependenciesMet = Object.entries(opts.ruleset.dependsOn).every(
                    ([parentTestId, validBuckets]) => {
                        const allTests: Ruleset['abTests'] = this.opts.config.ruleset?.abTests;
                        const parentRuleset = allTests?.[parentTestId];
                        if (!parentRuleset) {
                            throw new Error(`Parent test does not exist: ${parentTestId}`);
                        }

                        const parentAssignment = this.getAssignment({
                            isNewUser: opts.isNewUser,
                            userId,
                            testId: parentTestId,
                            ruleset: parentRuleset,
                            dynamicConfig: opts.dynamicConfig,
                            currentAssignments: opts.currentAssignments,
                            userState: opts.userState,
                        });

                        return parentAssignment && validBuckets.includes(parentAssignment.bucketId);
                    },
                );

                if (!allDependenciesMet) {
                    return undefined;
                }
            }

            // Manually assigned tests allow user lists to operate.
            if (opts.ruleset.assignManually) {
                // this returned value should not be used, because it does not represent actual manual assignment, just that a test _should_ be manually
                // assigned according to the ruleset
                return {
                    type: 'manual-inactive',
                    bucketId: opts.ruleset.buckets[0]!.id,
                    newUsersOnly: !!opts.ruleset.newUsersOnly,
                };
            }

            // Handle conditional assignment
            if (opts.ruleset.assignIf && !currentAssignment) {
                if (opts.userState === undefined || opts.ruleset.assignIf(opts.userState) === false) {
                    return undefined;
                }
            }
        }

        // Rolled out. Pick a random bucket.
        const bucketId = pickABTestBucket(userId, testId, { [testId]: opts.ruleset });

        const isRolledOutToNewUsersOnly =
            isDynamicallyConfigurableTest &&
            testConfig?.newUsersRollOut !== undefined &&
            !isTestRolledOutToUser({ rollOut: testConfig.rollOut, testId, userId });

        const newUsersOnly = opts.ruleset.newUsersOnly || isRolledOutToNewUsersOnly;

        return { type: 'automatic', bucketId, newUsersOnly };
    }
}
